import jsPDF from 'jspdf';

// ============================================================
// utils/certificate.js
// ------------------------------------------------------------
// Generates a landscape A4 certificate PDF and returns it as a Blob
// (same contract the app already expects: certificateService.uploadPdf
// takes this blob and puts it in Supabase storage).
//
// Needs `jspdf` installed:
//   npm install jspdf
//
// LOGO: there's no image asset wired up yet, so this draws a simple
// text-based "Wiiz" wordmark instead of an <img>/logo file. Swap
// drawWiizLogo() for doc.addImage(logoBase64, 'PNG', x, y, w, h) once
// you have an actual logo file (convert it to base64 first).
//
// Also exports downloadBlob() — a tiny helper the Certificates /
// PartnerCertificates pages should use to actually trigger a browser
// download when they fall back to generating the PDF on the fly
// (previously the blob was generated but never saved to disk).
// ============================================================

const BRAND_PINK = [230, 30, 90];
const INK = [20, 20, 20];
const MUTED = [110, 110, 110];

function drawWiizLogo(doc, x, y) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.setTextColor(...BRAND_PINK);
  doc.text('Wii', x, y);
  const wiiWidth = doc.getTextWidth('Wii');
  doc.setTextColor(...INK);
  doc.text('Z', x + wiiWidth, y);
}

function drawBorder(doc, pageWidth, pageHeight) {
  doc.setDrawColor(...BRAND_PINK);
  doc.setLineWidth(3);
  doc.rect(24, 24, pageWidth - 48, pageHeight - 48);

  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.75);
  doc.rect(34, 34, pageWidth - 68, pageHeight - 68);
}

/**
 * Builds a certificate PDF and resolves with a Blob.
 * @param {Object} params
 * @param {string} params.personName
 * @param {string} params.contentName   - course / content title
 * @param {string} params.certificateId - short display id (e.g. CERT-2026-XXXXXX)
 * @param {string} params.uuid          - internal uuid, used in the verify link
 * @param {string} params.verifyUrl     - full verification URL
 * @returns {Promise<Blob>}
 */
export async function certificatePdf({ personName, contentName, certificateId, uuid, verifyUrl }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const centerX = pageWidth / 2;

  drawBorder(doc, pageWidth, pageHeight);
  drawWiizLogo(doc, 56, 76);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...MUTED);
  doc.text('CERTIFICATE OF COMPLETION', centerX, 140, { align: 'center' });

  doc.setDrawColor(...BRAND_PINK);
  doc.setLineWidth(1);
  doc.line(centerX - 60, 150, centerX + 60, 150);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(32);
  doc.setTextColor(...INK);
  doc.text(personName || 'Participant', centerX, 195, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(13);
  doc.setTextColor(...MUTED);
  doc.text('has successfully completed', centerX, 222, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...INK);
  doc.text(contentName || 'Course', centerX, 252, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text(`Certificate ID: ${certificateId || uuid}`, centerX, pageHeight - 82, { align: 'center' });
  if (verifyUrl) {
    doc.text(`Verify at: ${verifyUrl}`, centerX, pageHeight - 66, { align: 'center' });
  }

  // signature-style footer line, purely decorative
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.5);
  doc.line(pageWidth - 240, pageHeight - 100, pageWidth - 80, pageHeight - 100);
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text('Authorized Signatory · Wiiz', pageWidth - 160, pageHeight - 86, { align: 'center' });

  return doc.output('blob');
}

/**
 * Triggers a browser download for a Blob. Use this after certificatePdf()
 * when there's no pdf_url yet (on-the-fly fallback in Certificates.jsx /
 * PartnerCertificates.jsx download handlers).
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'certificate.pdf';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}